import type { Shape } from "@gettactic/map-builder/src/types";
import type { Box } from "./spacingSnap";

/** One side of a straight, axis-aligned wall segment. */
export interface WallFace {
  /** The axis the face is perpendicular to. */
  axis: "x" | "y";
  /** Coordinate of the face on that axis. */
  at: number;
  /** Extent of the segment along the wall. */
  lo: number;
  hi: number;
  /** Which side of the wall the face is: `near` faces smaller
   *  coordinates, `far` larger ones. */
  side: "near" | "far";
}

export interface WallFlushSnap {
  offset: number;
  /** The face line, for the overlay. */
  guide: number;
}

/** Faces of a wall path's horizontal and vertical segments. Angled
 *  segments have no face a box can sit flush against. */
export const wallFaces = (shape: Shape): WallFace[] => {
  if (shape.type !== "wall-path") return [];
  const half = shape.thickness / 2;
  const faces: WallFace[] = [];
  const count = shape.closed
    ? shape.vertices.length
    : shape.vertices.length - 1;
  for (let index = 0; index < count; index += 1) {
    const from = shape.vertices[index];
    const to = shape.vertices[(index + 1) % shape.vertices.length];
    if (Math.abs(from.x - to.x) < 0.01) {
      const lo = Math.min(from.y, to.y);
      const hi = Math.max(from.y, to.y);
      faces.push({ axis: "x", at: from.x - half, lo, hi, side: "near" });
      faces.push({ axis: "x", at: from.x + half, lo, hi, side: "far" });
    } else if (Math.abs(from.y - to.y) < 0.01) {
      const lo = Math.min(from.x, to.x);
      const hi = Math.max(from.x, to.x);
      faces.push({ axis: "y", at: from.y - half, lo, hi, side: "near" });
      faces.push({ axis: "y", at: from.y + half, lo, hi, side: "far" });
    }
  }
  return faces;
};

/**
 * Snaps the moving box flush against the closest wall face on the axis,
 * when the box runs alongside that segment: its leading edge against the
 * face on the wall's far side, its trailing edge against the near side,
 * so the box ends up beside the wall and never on top of it.
 */
export const snapToWallFaces = (
  moving: Box,
  faces: WallFace[],
  axis: "x" | "y",
  threshold: number
): WallFlushSnap | null => {
  const [leading, trailing] =
    axis === "x" ? [moving.left, moving.right] : [moving.top, moving.bottom];
  const alongLo = axis === "x" ? moving.top : moving.left;
  const alongHi = axis === "x" ? moving.bottom : moving.right;
  let best: WallFlushSnap | null = null;
  let bestDiff = threshold;
  for (const face of faces) {
    if (face.axis !== axis) continue;
    if (face.hi <= alongLo || face.lo >= alongHi) continue;
    const edge = face.side === "far" ? leading : trailing;
    const diff = Math.abs(face.at - edge);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = { offset: face.at - edge, guide: face.at };
    }
  }
  return best;
};
